/**
 * /promoters/apply — Multi-step KYC + NDA flow (Phase P2)
 * ─────────────────────────────────────────────────────────────────────────
 * Step 1: Invite code (auto-filled from `?invite=`)
 * Step 2: Phone + OTP verification
 * Step 3: KYC details (Name, Instagram, PAN, Bank, IFSC, PAN file upload)
 * Step 4: NDA scroll-to-bottom + typed signature
 * Step 5: Submit → success screen
 *
 * Hard rules enforced client-side (server re-validates):
 *   • PAN/IFSC format checks before "Next"
 *   • PAN file ≤ 4MB, JPEG/PNG/WEBP/PDF
 *   • NDA scroll-to-bottom must complete before signature input enables
 *   • Typed signature must equal full name (case-insensitive)
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { promoterApi } from '../lib/api';
import api from '../lib/api';
import {
  Check, ArrowRight, Upload, ShieldCheck, FileText, Phone, ArrowLeft,
} from 'lucide-react';

const PAN_REGEX  = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const BANK_REGEX = /^[0-9]{6,20}$/;

const STEPS = [
  { id: 'invite',  label: 'Invite' },
  { id: 'phone',   label: 'Verify WhatsApp' },
  { id: 'kyc',     label: 'KYC' },
  { id: 'nda',     label: 'Sign NDA' },
  { id: 'done',    label: 'Done' },
];

function Stepper({ current }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-8 flex-wrap">
      {STEPS.slice(0, 4).map((s, i) => {
        const active = i === current;
        const done   = i < current;
        return (
          <div key={s.id} className="flex items-center gap-2">
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full border ${
              done   ? 'border-[#2FBF71]/40 bg-[#0E2E1E] text-[#2FBF71]'
                : active ? 'border-[#D4A843]/40 bg-[#D4A843]/10 text-[#D4A843]'
                  : 'border-[#2A2A2A] bg-[#0F0F0F] text-[#6F6F6F]'
            }`}>
              <span className="w-5 h-5 rounded-full bg-current/10 flex items-center justify-center text-[10px]">
                {done ? <Check size={11} /> : i + 1}
              </span>
              <span className="text-[10px] uppercase tracking-[1px]">{s.label}</span>
            </div>
            {i < 3 && <span className="text-[#2A2A2A]">—</span>}
          </div>
        );
      })}
    </div>
  );
}

export default function PromoterApplyPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialInvite = (params.get('invite') || '').toUpperCase();

  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  // Step 1: invite
  const [invite, setInvite] = useState(initialInvite);
  const [inviteValid, setInviteValid] = useState(false);

  // Step 2: phone + OTP
  const [phone, setPhone] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);

  // Step 3: KYC
  const [name, setName] = useState('');
  const [handle, setHandle] = useState('');
  const [pan, setPan] = useState('');
  const [bank, setBank] = useState('');
  const [ifsc, setIfsc] = useState('');
  const [panFile, setPanFile] = useState(null);

  // Step 4: NDA
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [signature, setSignature] = useState('');
  const ndaRef = useRef(null);

  const onScroll = (e) => {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 8) {
      setScrolledToBottom(true);
    }
  };

  // Auto-validate the invite if it came in via the URL
  useEffect(() => {
    if (!initialInvite) return;
    (async () => {
      try {
        const { data } = await promoterApi.publicValidateInvite(initialInvite);
        if (data?.ok) {
          setInviteValid(true);
          // Skip Step 1 if pre-validated
          setStep(1);
        }
      } catch {
        // Stay on step 0; user can retype
        toast.error('Invite code is invalid or expired. Please re-enter.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialInvite]);

  // ── Step 1 actions ──────────────────────────────────────────────────────
  const validateInvite = async () => {
    const s = invite.trim().toUpperCase();
    if (!s) { toast.error('Enter your invite code'); return; }
    setBusy(true);
    try {
      const { data } = await promoterApi.publicValidateInvite(s);
      if (data?.ok) {
        setInviteValid(true);
        setInvite(data.code);
        setStep(1);
      }
    } catch (err) {
      const c = err?.response?.data?.detail?.code;
      toast.error(c === 'RATE_LIMIT' ? 'Too many attempts. Try again in 10 min.' : 'Invalid invite code.');
    } finally {
      setBusy(false);
    }
  };

  // ── Step 2 actions ──────────────────────────────────────────────────────
  const sendOtp = async () => {
    const p = phone.trim();
    if (!/^\d{10}$/.test(p) && !/^\+91\d{10}$/.test(p)) {
      toast.error('Enter a valid 10-digit WhatsApp number');
      return;
    }
    const phoneNorm = p.startsWith('+91') ? p : `+91${p}`;
    setPhone(phoneNorm);
    setBusy(true);
    try {
      // WhatsApp-only OTP — no SMS fallback. We want a verified WhatsApp
      // number on file so downstream commission notifications reach the
      // promoter reliably.
      await api.post('/api/auth/send-otp-whatsapp', { phone: phoneNorm });
      setOtpSent(true);
      toast.success(`OTP sent on WhatsApp to ${phoneNorm}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send WhatsApp OTP. Please make sure the number has WhatsApp installed.');
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = () => {
    if (!/^\d{4,8}$/.test(otp.trim())) { toast.error('Enter the 6-digit code'); return; }
    // We don't pre-verify here — the final submit will re-check the OTP
    // server-side. But we lock the form so the user can proceed.
    setOtpVerified(true);
    setStep(2);
  };

  // ── Step 3 → Step 4 ─────────────────────────────────────────────────────
  const proceedFromKyc = () => {
    const errs = [];
    if (!name.trim() || name.trim().length < 2) errs.push('Full name is required');
    if (!PAN_REGEX.test(pan.toUpperCase()))     errs.push('PAN must look like AAAAA9999A');
    if (!BANK_REGEX.test(bank))                 errs.push('Bank account must be 6–20 digits');
    if (!IFSC_REGEX.test(ifsc.toUpperCase()))   errs.push('IFSC must look like SBIN0001234');
    if (!panFile)                               errs.push('Upload your PAN image / PDF');
    else if (panFile.size > 4 * 1024 * 1024)    errs.push('PAN file must be ≤ 4 MB');
    if (errs.length) { toast.error(errs[0]); return; }
    setStep(3);
  };

  // ── Step 4: submit ──────────────────────────────────────────────────────
  const finalSubmit = async () => {
    if (!scrolledToBottom)                 { toast.error('Please read the NDA to the bottom'); return; }
    if (signature.trim().toLowerCase() !== name.trim().toLowerCase()) {
      toast.error('Signature must match your full name exactly'); return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append('invite_code',         invite);
      form.append('name',                name.trim());
      form.append('phone',               phone);
      form.append('otp',                 otp.trim());
      form.append('instagram_handle',    handle.trim());
      form.append('pan_number',          pan.toUpperCase());
      form.append('bank_account_number', bank);
      form.append('ifsc_code',           ifsc.toUpperCase());
      form.append('nda_signature_text',  signature.trim());
      form.append('pan_file',            panFile, panFile.name);
      const { data } = await promoterApi.publicSubmitApplication(form);
      if (data?.ok) {
        toast.success('Application submitted!');
        setStep(4);
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = (typeof d === 'object' && d?.message) ? d.message : (d || 'Submission failed');
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────
  const progress = Math.round((step / (STEPS.length - 1)) * 100);

  return (
    <div className="min-h-screen text-[#F2F2F2]" style={{ backgroundColor: '#0A0A0A' }}>
      <div className="max-w-2xl mx-auto px-6 py-10">
        <button onClick={() => navigate('/promoters')} className="text-[#B9B9B9] hover:text-[#F2F2F2] text-sm flex items-center gap-1 mb-6">
          <ArrowLeft size={14} /> back
        </button>
        <h1 className="font-['EB_Garamond'] text-3xl md:text-4xl text-center mb-2">Promoter Application</h1>
        <p className="text-center text-[#B9B9B9] text-sm mb-6">{step < 4 ? 'A premium program — KYC + NDA are mandatory.' : ''}</p>
        <Progress value={progress} className="h-1 bg-[#1A1A1A] mb-2" />
        <Stepper current={Math.min(step, 3)} />

        {/* Step 0 — Invite */}
        {step === 0 && (
          <div className="space-y-4 max-w-md mx-auto" data-testid="step-invite">
            <Label className="text-xs text-[#B9B9B9]">Invite code</Label>
            <Input
              value={invite}
              onChange={(e) => setInvite(e.target.value.toUpperCase())}
              placeholder="INV-XXXXX"
              className="bg-[#0F0F0F] border-[#2A2A2A] text-[#F2F2F2] text-center font-mono uppercase tracking-widest"
              onKeyDown={(e) => e.key === 'Enter' && validateInvite()}
              data-testid="invite-code-input-apply"
            />
            <Button onClick={validateInvite} disabled={busy} className="w-full bg-[#D4A843] hover:bg-[#E0B450] text-[#120F08]">
              {busy ? 'Checking…' : 'Continue'} <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        )}

        {/* Step 1 — WhatsApp + OTP */}
        {step === 1 && (
          <div className="space-y-4 max-w-md mx-auto" data-testid="step-phone">
            <div>
              <Label className="text-xs text-[#B9B9B9]">WhatsApp Number (we&apos;ll send a one-time code)</Label>
              <div className="flex gap-2 mt-1">
                <span className="w-14 flex items-center justify-center bg-[#0F0F0F] border border-[#2A2A2A] rounded-md text-[#B9B9B9] text-sm">+91</span>
                <Input
                  value={phone.replace(/^\+91/, '')}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="10-digit WhatsApp number"
                  className="flex-1 bg-[#0F0F0F] border-[#2A2A2A] text-[#F2F2F2]"
                  data-testid="phone-input"
                />
                <Button onClick={sendOtp} disabled={busy || otpSent} className="bg-[#1A1A1A] border border-[#2A2A2A] text-[#F2F2F2] hover:bg-[#2A2A2A]" data-testid="send-otp-button">
                  <Phone size={14} className="mr-1" /> {otpSent ? 'Sent' : 'Send via WhatsApp'}
                </Button>
              </div>
              <p className="text-[11px] text-[#7A7A72] mt-1.5">
                Number must have WhatsApp installed. We&apos;ll use it for commission updates &amp; payouts.
              </p>
            </div>
            {otpSent && (
              <div>
                <Label className="text-xs text-[#B9B9B9]">Enter 6-digit code from WhatsApp</Label>
                <Input
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="••••••"
                  className="mt-1 bg-[#0F0F0F] border-[#2A2A2A] text-[#F2F2F2] text-center text-xl tracking-widest"
                  data-testid="otp-input"
                />
                <Button onClick={verifyOtp} disabled={busy || otp.length < 6} className="w-full bg-[#D4A843] hover:bg-[#E0B450] text-[#120F08] mt-3" data-testid="verify-otp-button">
                  Continue <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step 2 — KYC */}
        {step === 2 && (
          <div className="space-y-4 max-w-xl mx-auto" data-testid="step-kyc">
            <div>
              <Label className="text-xs text-[#B9B9B9]">Full Name (as per PAN)</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Aarav Sharma" className="mt-1 bg-[#0F0F0F] border-[#2A2A2A] text-[#F2F2F2]" data-testid="kyc-name" />
            </div>
            <div>
              <Label className="text-xs text-[#B9B9B9]">Instagram handle (optional)</Label>
              <Input value={handle} onChange={(e) => setHandle(e.target.value.replace(/^@/, ''))} placeholder="aarav.shoot" className="mt-1 bg-[#0F0F0F] border-[#2A2A2A] text-[#F2F2F2]" data-testid="kyc-handle" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#B9B9B9]">PAN Number</Label>
                <Input value={pan} onChange={(e) => setPan(e.target.value.toUpperCase())} placeholder="ABCDE1234F" maxLength={10} className="mt-1 bg-[#0F0F0F] border-[#2A2A2A] text-[#F2F2F2] uppercase font-mono" data-testid="kyc-pan" />
              </div>
              <div>
                <Label className="text-xs text-[#B9B9B9]">IFSC Code</Label>
                <Input value={ifsc} onChange={(e) => setIfsc(e.target.value.toUpperCase())} placeholder="HDFC0001234" maxLength={11} className="mt-1 bg-[#0F0F0F] border-[#2A2A2A] text-[#F2F2F2] uppercase font-mono" data-testid="kyc-ifsc" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-[#B9B9B9]">Bank Account Number</Label>
              <Input value={bank} onChange={(e) => setBank(e.target.value.replace(/\D/g, '').slice(0, 20))} placeholder="6–20 digits" className="mt-1 bg-[#0F0F0F] border-[#2A2A2A] text-[#F2F2F2] font-mono" data-testid="kyc-bank" />
            </div>
            <div>
              <Label className="text-xs text-[#B9B9B9]">Upload PAN (image or PDF · ≤ 4 MB)</Label>
              <label className={`mt-1 flex items-center gap-3 px-3 py-3 border border-dashed rounded-md cursor-pointer ${panFile ? 'border-[#2FBF71]/40 bg-[#0E2E1E]' : 'border-[#2A2A2A] hover:border-[#D4A843]/40 bg-[#0F0F0F]'}`}>
                <input type="file" hidden accept="image/jpeg,image/png,image/webp,application/pdf" onChange={(e) => setPanFile(e.target.files?.[0] || null)} data-testid="kyc-pan-file" />
                <Upload size={16} className={panFile ? 'text-[#2FBF71]' : 'text-[#B9B9B9]'} />
                <span className="text-sm">{panFile ? panFile.name : 'Click to select file'}</span>
                {panFile && <span className="ml-auto text-[10px] text-[#6F6F6F]">{(panFile.size / 1024 / 1024).toFixed(2)} MB</span>}
              </label>
            </div>
            <Button onClick={proceedFromKyc} className="w-full bg-[#D4A843] hover:bg-[#E0B450] text-[#120F08]" data-testid="kyc-continue">
              Continue to NDA <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        )}

        {/* Step 3 — NDA */}
        {step === 3 && (
          <div className="space-y-4 max-w-2xl mx-auto" data-testid="step-nda">
            <div className="flex items-center gap-2 text-[#B9B9B9] text-xs">
              <ShieldCheck size={14} className="text-[#D4A843]" />
              <span>Scroll to the bottom to enable the signature field</span>
            </div>
            <div
              ref={ndaRef}
              onScroll={onScroll}
              className="border border-[#2A2A2A] rounded-lg bg-[#0F0F0F] p-4 text-[#B9B9B9] text-sm leading-relaxed h-72 overflow-y-auto"
              data-testid="nda-scroll-area"
            >
              <h3 className="text-[#F2F2F2] font-medium mb-2">Promoter Non-Disclosure Agreement</h3>
              <p className="mb-2">This Agreement is entered into between <b>Scratch Unleashed Pvt. Ltd.</b> (&quot;Company&quot;) and the individual identified below (&quot;Promoter&quot;).</p>
              <p className="mb-2"><b>1. Confidentiality.</b> The Promoter shall not disclose, share, or publish any confidential information, including customer data, internal pricing, marketing strategies, or business processes, without prior written consent of the Company.</p>
              <p className="mb-2"><b>2. Compliance.</b> The Promoter shall comply with all applicable laws including but not limited to data protection, anti-bribery, and tax regulations of India.</p>
              <p className="mb-2"><b>3. Commission.</b> The Promoter acknowledges that commission is calculated as per the active commission model and may be revised by the Company on a per-event or per-month basis.</p>
              <p className="mb-2"><b>4. Termination.</b> Either party may terminate this engagement at will. Upon termination, the Promoter shall return or destroy all confidential materials within 7 days.</p>
              <p className="mb-2"><b>5. Disputes.</b> Any disputes arising under this Agreement shall be resolved through arbitration in Mumbai, India under the Arbitration &amp; Conciliation Act, 1996.</p>
              <p className="mb-2"><b>6. Independent Contractor.</b> The Promoter is an independent contractor. No employment, partnership, or joint venture is created.</p>
              <p className="mb-2"><b>7. Records.</b> A digitally signed and dated copy of this Agreement is preserved with the Company and constitutes valid evidence under the Information Technology Act, 2000.</p>
              <p className="mb-2"><b>8. Severability.</b> If any provision is held invalid, the remaining provisions continue to be in effect.</p>
              <p className="mt-3 text-[#6F6F6F] text-xs">— End of agreement —</p>
            </div>
            <div>
              <Label className="text-xs text-[#B9B9B9]">Type your full name as digital signature</Label>
              <Input
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                disabled={!scrolledToBottom}
                placeholder={scrolledToBottom ? `${name}` : 'Scroll the NDA to enable…'}
                className="mt-1 bg-[#0F0F0F] border-[#2A2A2A] text-[#F2F2F2] disabled:opacity-50"
                data-testid="nda-signature-input"
              />
              <p className="text-[10px] text-[#6F6F6F] mt-1">By typing your name you agree to the terms above. IP, user-agent, and a SHA-256 fingerprint are recorded.</p>
            </div>
            <Button onClick={finalSubmit} disabled={busy || !scrolledToBottom} className="w-full bg-[#D4A843] hover:bg-[#E0B450] text-[#120F08]" data-testid="submit-application-button">
              {busy ? 'Submitting…' : 'Submit Application'} <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        )}

        {/* Step 4 — Done */}
        {step === 4 && (
          <div className="max-w-md mx-auto text-center py-10" data-testid="step-done">
            <div className="w-16 h-16 rounded-full bg-[#0E2E1E] border border-[#2FBF71]/30 flex items-center justify-center mx-auto mb-4">
              <Check size={28} className="text-[#2FBF71]" />
            </div>
            <h2 className="font-['EB_Garamond'] text-3xl mb-2">Application Received</h2>
            <p className="text-[#B9B9B9] mb-6">We&apos;ll review your KYC and revert within 48 hours. Approved promoters receive a branded link instantly via WhatsApp.</p>
            <Button onClick={() => navigate('/')} className="bg-[#D4A843] hover:bg-[#E0B450] text-[#120F08]">
              Back to home
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
